/**
 * Everything members tell us, across every member: bug reports, thumbs on chat
 * replies, outfits and co-ord sets, newest first.
 *
 * A bug report carries what the app sent with it (the screen the member came
 * from, the app build and update, the device and up to three images) and a
 * triage status an operator sets here, with a note.
 *
 * The read and the triage write are looked up by name in the generated client,
 * so a client without one of them shows that part as unavailable instead of
 * failing the page.
 */

import { useState } from 'react';
import * as api from '@closet-theory/api';
import { Link, useSearchParams } from 'react-router';
import { Minus, ThumbsDown, ThumbsUp } from '@untitledui/icons';
import { Badge } from '@ui/components/base/badges/badges';
import { Button } from '@ui/components/base/buttons/button';
import { CheckboxField, SelectField, TextAreaField } from '@ui/console/forms';
import { formatRelative, formatTimestamp, humanize } from '@ui/console/format';
import { Section } from '@ui/console/primitives';
import { cx } from '@ui/utils/cx';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Absent, InsightState, useInsight, type Read } from './consumer-insights';
import { Filters, Page, Pager } from './components';
import { GarmentImage } from './garment-image';
import { errorDetail } from './operation-policy';
import { CopyableId } from './references';

/**
 * The one place that knows what the generated client calls the read. A
 * generated name ends in its path and method, so the suffix identifies
 * `GET /api/v1/internal/feedback` whatever the prefix becomes.
 */
export const FEEDBACK_OPERATION = /ApiV1InternalFeedbackGet$/;

/** `PATCH /api/v1/internal/feedback/bug-reports/{report_id}`, found the same way. */
export const TRIAGE_OPERATION = /ApiV1InternalFeedbackBugReportsReportIdPatch$/;

export type FeedbackKind = 'bug_report' | 'chat_reply' | 'outfit' | 'garment_set';
export type FeedbackSentiment = 'positive' | 'negative' | 'neutral';
export type BugReportStatus = 'new' | 'in_progress' | 'resolved' | 'wont_fix';

export type FeedbackAttachment = { ref: string; url: string | null; thumbnail_url: string | null };

/** What the app reports about its own build and over-the-air update. */
export type FeedbackApp = {
  app_version?: string | null;
  build_number?: string | null;
  update_id?: string | null;
  runtime_version?: string | null;
  channel?: string | null;
};

export type FeedbackItem = {
  feedback_id: string;
  kind: FeedbackKind;
  created_at: string;
  member_id: string | null;
  member_name: string | null;
  sentiment: FeedbackSentiment | null;
  rating_raw: string | null;
  text: string | null;
  subject: { type: string | null; id: string | null; title: string | null };
  context: {
    job_id?: string | null;
    outfit_id?: string | null;
    set_id?: string | null;
    actually_worn?: boolean | null;
    worn_at?: string | null;
    device_info?: Record<string, string | number | null> | null;
  };
  // Bug reports only; absent or null on every other kind.
  member_email?: string | null;
  status?: BugReportStatus | null;
  triage_note?: string | null;
  triaged_by?: string | null;
  triaged_at?: string | null;
  screen?: string | null;
  app?: FeedbackApp | null;
  attachments?: FeedbackAttachment[] | null;
};

export type FeedbackPage = { items: FeedbackItem[]; total: number; limit: number; offset: number };

type FeedbackQuery = {
  limit: number;
  offset: number;
  kind?: string[];
  sentiment?: FeedbackSentiment;
  has_text?: boolean;
  status?: BugReportStatus[];
  member_id?: string;
  since?: string;
  until?: string;
};

type FeedbackOperation = (options: { query: FeedbackQuery }) => Promise<Read<FeedbackPage>>;

type TriageOperation = (options: {
  path: { report_id: string };
  body: { status: BugReportStatus; note?: string };
}) => Promise<Read<FeedbackItem>>;

function findOperation<T>(pattern: RegExp): T | undefined {
  return Object.entries(api as Record<string, unknown>).find(
    ([name, value]) => pattern.test(name) && typeof value === 'function',
  )?.[1] as T | undefined;
}

export function feedbackOperation(): FeedbackOperation | undefined {
  return findOperation<FeedbackOperation>(FEEDBACK_OPERATION);
}

export function triageOperation(): TriageOperation | undefined {
  return findOperation<TriageOperation>(TRIAGE_OPERATION);
}

/** The report's own id: a bug report's feedback id is `bug_report:<report_id>`. */
export function reportId(item: FeedbackItem): string {
  return item.feedback_id.replace(/^bug_report:/, '');
}

const PAGE = 50;

const KIND_LABEL: Record<FeedbackKind, string> = {
  bug_report: 'Bug report',
  chat_reply: 'Chat reply',
  outfit: 'Outfit',
  garment_set: 'Co-ord set',
};

const KIND_COLOR = {
  bug_report: 'orange',
  chat_reply: 'blue',
  outfit: 'purple',
  garment_set: 'pink',
} as const satisfies Record<FeedbackKind, string>;

const KIND_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'bug_report', label: 'Bug reports' },
  { value: 'chat_reply', label: 'Chat replies' },
  { value: 'outfit', label: 'Outfits' },
  { value: 'garment_set', label: 'Co-ord sets' },
];

const SENTIMENT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'neutral', label: 'Neutral' },
];

const STATUS_LABEL: Record<BugReportStatus, string> = {
  new: 'New',
  in_progress: 'In progress',
  resolved: 'Resolved',
  wont_fix: "Won't fix",
};

const STATUS_COLOR = {
  new: 'orange',
  in_progress: 'blue',
  resolved: 'success',
  wont_fix: 'gray',
} as const satisfies Record<BugReportStatus, string>;

const STATUSES = Object.keys(STATUS_LABEL) as BugReportStatus[];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  ...STATUSES.map((value) => ({ value, label: STATUS_LABEL[value] })),
];

function isStatus(value: string): value is BugReportStatus {
  return value in STATUS_LABEL;
}

function isKind(value: string): value is FeedbackKind {
  return value in KIND_LABEL;
}

function isSentiment(value: string): value is FeedbackSentiment {
  return value === 'positive' || value === 'negative' || value === 'neutral';
}

/** The member's page, opened on the tab the feedback was about. */
function memberHref(item: FeedbackItem & { member_id: string }): string {
  const member = `/consumers/${encodeURIComponent(item.member_id)}`;
  switch (item.kind) {
    case 'chat_reply':
      return `${member}?tab=chat`;
    case 'outfit': {
      const outfit =
        item.context.outfit_id ?? (item.subject.type === 'outfit' ? item.subject.id : null);
      return outfit
        ? `${member}?tab=outfits&outfit=${encodeURIComponent(outfit)}`
        : `${member}?tab=outfits`;
    }
    // A member's co-ord sets sit on their Wardrobe tab.
    case 'garment_set':
      return `${member}?tab=wardrobe`;
    default:
      return member;
  }
}

const SENTIMENT = {
  positive: { label: 'Positive', Icon: ThumbsUp, className: 'text-success-primary' },
  negative: { label: 'Negative', Icon: ThumbsDown, className: 'text-error-primary' },
  neutral: { label: 'Neutral', Icon: Minus, className: 'text-tertiary' },
} as const;

function SentimentIndicator({
  sentiment,
  rating,
}: {
  sentiment: FeedbackSentiment | null;
  rating: string | null;
}) {
  if (!sentiment)
    return (
      <span className="text-xs text-tertiary">{rating ? `Rated ${rating}` : 'No rating'}</span>
    );
  const { label, Icon, className } = SENTIMENT[sentiment];
  return (
    <span
      data-sentiment={sentiment}
      className={cx('inline-flex items-center gap-1 text-xs font-semibold', className)}
      {...(rating ? { title: `The member's rating: ${rating}` } : {})}
    >
      <Icon className="size-4" aria-hidden="true" />
      {label}
    </span>
  );
}

/** Past this the comment is clamped behind "Show more". */
const LONG_TEXT_CHARS = 320;
const LONG_TEXT_LINES = 5;

function Comment({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > LONG_TEXT_CHARS || text.split('\n').length > LONG_TEXT_LINES;
  return (
    <div className="flex flex-col items-start gap-1">
      <p
        className={cx(
          'text-sm break-words whitespace-pre-line text-primary',
          long && !open && 'line-clamp-4',
        )}
      >
        {text}
      </p>
      {long ? (
        <Button size="sm" color="link-gray" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </div>
  );
}

function Details({ label, entries }: { label: string; entries: Array<[string, string]> }) {
  return (
    <dl aria-label={label} className="flex flex-wrap gap-2 text-xs">
      {entries.map(([key, value]) => (
        <div key={key} className="rounded-md bg-secondary px-2 py-1 text-secondary">
          <dt className="inline text-tertiary">{key}: </dt>
          <dd className="inline font-medium">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const APP_LABEL: Array<[keyof FeedbackApp, string]> = [
  ['app_version', 'Version'],
  ['build_number', 'Build'],
  ['channel', 'Channel'],
  ['runtime_version', 'Runtime'],
  ['update_id', 'Update'],
];

const DEVICE_LABEL: Record<string, string> = {
  platform: 'Platform',
  os_version: 'OS',
  model: 'Model',
};

function present(value: unknown): value is string | number {
  return (typeof value === 'string' && value.trim() !== '') || typeof value === 'number';
}

function Attachments({ attachments }: { attachments: FeedbackAttachment[] }) {
  return (
    <ul aria-label="Attachments" className="flex flex-wrap gap-2">
      {attachments.map((attachment, index) => {
        const thumb = attachment.thumbnail_url ?? attachment.url;
        const label = `Attachment ${index + 1}`;
        return (
          <li key={attachment.ref}>
            {attachment.url && thumb ? (
              <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block outline-brand focus-visible:outline-2"
              >
                <GarmentImage
                  url={thumb}
                  refPresent={attachment.ref}
                  alt={label}
                  className="size-24 rounded-lg border border-secondary"
                />
              </a>
            ) : (
              <span className="flex size-24 items-center justify-center rounded-lg border border-secondary text-center text-xs text-tertiary">
                {label} could not be loaded
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Triage({ item, onSaved }: { item: FeedbackItem; onSaved: (next: FeedbackItem) => void }) {
  const triage = triageOperation();
  const [status, setStatus] = useState<BugReportStatus>(item.status ?? 'new');
  const [note, setNote] = useState(item.triage_note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const noteChanged = note.trim() !== (item.triage_note ?? '').trim();
  const changed = status !== (item.status ?? 'new') || noteChanged;

  if (!triage)
    return (
      <p className="text-xs text-tertiary">
        Triage isn&rsquo;t available on this server yet. Nothing is wrong with your access.
      </p>
    );

  const save = async () => {
    setBusy(true);
    setError(undefined);
    configureAdminApi();
    const result = await triage({
      path: { report_id: reportId(item) },
      // The server keeps the note when it is left out and clears it when blank.
      body: { status, ...(noteChanged ? { note: note.trim() } : {}) },
    }).catch(() => undefined);
    setBusy(false);
    if (!result) {
      setError('The server could not be reached. The report is unchanged.');
      return;
    }
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) {
      setError(errorDetail(result.error, 'The triage was not saved. The report is unchanged.'));
      return;
    }
    onSaved(result.data);
  };

  return (
    <form
      aria-label="Triage"
      className="flex flex-col gap-2 border-t border-secondary pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <SelectField
          label="Status"
          value={status}
          options={STATUSES.map((value) => ({ value, label: STATUS_LABEL[value] }))}
          onChange={(event) => {
            const next = event.target.value;
            if (isStatus(next)) setStatus(next);
          }}
        />
        <Button type="submit" size="sm" color="primary" isDisabled={busy || !changed}>
          {busy ? 'Saving…' : 'Save triage'}
        </Button>
      </div>
      <TextAreaField
        label="Triage note"
        hint="What was found or done. Other operators see it here."
        rows={2}
        value={note}
        onChange={(event) => setNote(event.currentTarget.value)}
      />
      {error ? (
        <p role="alert" className="text-xs text-error-primary">
          {error}
        </p>
      ) : null}
      {item.triaged_at ? (
        <p className="text-xs text-tertiary">
          Last triaged{' '}
          <time dateTime={item.triaged_at} title={formatTimestamp(item.triaged_at)}>
            {formatRelative(item.triaged_at)}
          </time>
          {item.triaged_by ? (
            <>
              {' '}
              by <CopyableId value={item.triaged_by} label="operator id" />
            </>
          ) : null}
        </p>
      ) : null}
    </form>
  );
}

function BugReportDetails({
  item,
  onSaved,
}: {
  item: FeedbackItem;
  onSaved: (next: FeedbackItem) => void;
}) {
  const device = Object.entries(item.context.device_info ?? {})
    .filter((entry): entry is [string, string | number] => present(entry[1]))
    // The app's first builds sent the OS version as `version`.
    .map(([key, value]): [string, string] => [
      DEVICE_LABEL[key] ?? (key === 'version' ? 'OS' : humanize(key)),
      String(value),
    ]);
  const app = APP_LABEL.flatMap(([key, label]): Array<[string, string]> => {
    const value = item.app?.[key];
    return present(value) ? [[label, String(value)]] : [];
  });
  const attachments = item.attachments ?? [];
  return (
    <div className="flex flex-col gap-2">
      {item.member_email || item.screen?.trim() ? (
        <Details
          label="Report"
          entries={[
            ...(item.member_email
              ? ([['Email', item.member_email]] as Array<[string, string]>)
              : []),
            ...(item.screen?.trim()
              ? ([['Sent from', item.screen.trim()]] as Array<[string, string]>)
              : []),
          ]}
        />
      ) : null}
      {app.length ? <Details label="App" entries={app} /> : null}
      {device.length ? <Details label="Device" entries={device} /> : null}
      {attachments.length ? <Attachments attachments={attachments} /> : null}
      <Triage item={item} onSaved={onSaved} />
    </div>
  );
}

function FeedbackEntry({ item: initial }: { item: FeedbackItem }) {
  // A triage save replaces the entry with what the server returned.
  const [item, setItem] = useState(initial);
  const text = item.text?.trim();
  const worn = item.context.actually_worn;
  return (
    <li
      data-feedback-kind={item.kind}
      className="flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            {item.member_id ? (
              <Link
                to={memberHref({ ...item, member_id: item.member_id })}
                className="text-sm font-medium text-brand-secondary outline-brand focus-visible:outline-2"
              >
                {item.member_name?.trim() || 'Unnamed member'}
              </Link>
            ) : (
              <span className="text-sm font-medium text-tertiary">Deleted member</span>
            )}
            <Badge type="pill-color" size="sm" color={KIND_COLOR[item.kind] ?? 'gray'}>
              {KIND_LABEL[item.kind] ?? humanize(item.kind)}
            </Badge>
            {item.kind === 'bug_report' && item.status && isStatus(item.status) ? (
              <span data-bug-status={item.status}>
                <Badge type="pill-color" size="sm" color={STATUS_COLOR[item.status]}>
                  {STATUS_LABEL[item.status]}
                </Badge>
              </span>
            ) : null}
          </div>
          {item.subject.title?.trim() ? (
            <h3 className="text-md font-semibold break-words text-primary">
              {item.subject.title.trim()}
            </h3>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <SentimentIndicator sentiment={item.sentiment} rating={item.rating_raw} />
          <time
            dateTime={item.created_at}
            title={formatTimestamp(item.created_at)}
            className="text-xs text-tertiary"
          >
            {formatRelative(item.created_at)}
          </time>
        </div>
      </div>

      {text ? <Comment text={text} /> : <p className="text-sm text-tertiary">No comment left.</p>}

      {worn === true || worn === false ? (
        <p className="text-xs text-tertiary">
          {worn
            ? `Worn${item.context.worn_at ? ` ${formatRelative(item.context.worn_at)}` : ''}`
            : 'Not worn'}
        </p>
      ) : null}

      {item.kind === 'bug_report' ? <BugReportDetails item={item} onSaved={setItem} /> : null}

      <div className="flex flex-wrap items-center gap-1 text-xs text-quaternary">
        <span>Reference</span>
        <CopyableId value={item.feedback_id} label="feedback id" />
      </div>
    </li>
  );
}

export function FeedbackList({ items }: { items: FeedbackItem[] }) {
  return (
    <ol className="flex flex-col gap-3" aria-label="Member feedback">
      {items.map((item) => (
        <FeedbackEntry key={`${item.kind}-${item.feedback_id}`} item={item} />
      ))}
    </ol>
  );
}

function FeedbackRead({ read }: { read: FeedbackOperation }) {
  // Filters live in the URL so a view can be sent to someone else or reloaded
  // without losing what you were looking at.
  const [params, setParams] = useSearchParams();
  const rawKind = params.get('type') ?? '';
  const kind = isKind(rawKind) ? rawKind : '';
  const rawSentiment = params.get('sentiment') ?? '';
  const sentiment = isSentiment(rawSentiment) ? rawSentiment : '';
  const withComments = params.get('comments') === '1';
  const rawStatus = params.get('status') ?? '';
  const status = isStatus(rawStatus) ? rawStatus : '';
  const offset = Math.max(0, Number.parseInt(params.get('offset') ?? '0', 10) || 0);

  const update = (next: {
    kind?: string;
    sentiment?: string;
    withComments?: boolean;
    status?: string;
    offset?: number;
  }) => {
    const type = next.kind ?? kind;
    const mood = next.sentiment ?? sentiment;
    const comments = next.withComments ?? withComments;
    const triage = next.status ?? status;
    // A page offset belongs to the filters that produced it.
    const at = next.offset ?? 0;
    setParams(
      {
        ...(type ? { type } : {}),
        ...(mood ? { sentiment: mood } : {}),
        ...(comments ? { comments: '1' } : {}),
        ...(triage ? { status: triage } : {}),
        ...(at ? { offset: String(at) } : {}),
      },
      { replace: true },
    );
  };

  const source = useInsight<FeedbackPage>(
    () =>
      read({
        query: {
          limit: PAGE,
          offset,
          ...(kind ? { kind: [kind] } : {}),
          ...(sentiment ? { sentiment } : {}),
          ...(withComments ? { has_text: true } : {}),
          ...(status ? { status: [status] } : {}),
        },
      }),
    [read, kind, sentiment, withComments, status, offset],
  );

  return (
    <Section
      title="Recent member feedback"
      description="Newest first across all members. Open a member to see the feedback in context."
    >
      <Filters>
        <SelectField
          label="Type"
          value={kind}
          options={KIND_OPTIONS}
          onChange={(event) => update({ kind: event.target.value })}
        />
        <SelectField
          label="Sentiment"
          value={sentiment}
          options={SENTIMENT_OPTIONS}
          onChange={(event) => update({ sentiment: event.target.value })}
        />
        <SelectField
          label="Bug report status"
          hint="Shows bug reports only."
          value={status}
          options={STATUS_FILTER_OPTIONS}
          onChange={(event) => update({ status: event.target.value })}
        />
        <CheckboxField
          label="With comments only"
          checked={withComments}
          onChange={(event) => update({ withComments: event.currentTarget.checked })}
        />
      </Filters>
      <InsightState source={source} subject="member feedback">
        {(page) =>
          page.items.length ? (
            <div className="flex flex-col gap-3">
              <FeedbackList items={page.items} />
              <Pager
                offset={page.offset}
                limit={page.limit}
                total={page.total}
                onPage={(next) => update({ offset: next })}
              />
            </div>
          ) : (
            <Absent title="No member feedback matches these filters.">
              The read succeeded and returned no feedback to show. Set the filters back to
              &ldquo;All&rdquo; to see everything.
            </Absent>
          )
        }
      </InsightState>
    </Section>
  );
}

export function MemberFeedback() {
  const read = feedbackOperation();
  return (
    <Page
      title="Feedback"
      purpose="Read what members tell us: bug reports and their reactions to chat replies, outfits and co-ord sets. Triage bug reports here: set a status and leave a note."
    >
      {read ? (
        <FeedbackRead read={read} />
      ) : (
        <Absent title="Feedback isn't available on this server yet.">
          This screen will fill in once the server is updated. Nothing is wrong with your access.
        </Absent>
      )}
    </Page>
  );
}
